<template>
  <v-container fluid class="pa-0 bg-slate-50 min-h-screen" style="font-family: 'Inter', sans-serif;">
    <div class="p-4">
      <div class="max-w-7xl mx-auto space-y-8">
        
        <!-- HEADER -->
        <div class="mb-6">
          <h1 class="text-2xl font-bold text-slate-700">Gerenciar Prefeituras</h1>
          <p class="text-sm text-slate-500">Visualize e gerencie todas as prefeituras cadastradas</p>
        </div>

        <!-- LISTAGEM -->
        <section class="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <!-- BARRA DE FERRAMENTAS DA TABELA -->
            <div class="px-6 py-4 flex items-center justify-between" style="background-color: #333333;">
                <div class="flex items-center gap-3">
                  <v-icon color="orange" small>mdi-format-list-bulleted</v-icon>
                  <h2 class="text-white font-bold uppercase tracking-widest text-[11px]">PREFEITURAS CADASTRADAS</h2>
                </div>
                
                <div class="flex items-center gap-4 flex-1 justify-end">
                  <button 
                    @click="$router.push('/prefectures-manager/create')" 
                    class="bg-orange-500 hover:bg-orange-600 text-white shadow-lg flex items-center justify-center gap-2 rounded-lg px-6 py-2 transition-all w-fit max-max whitespace-nowrap"
                    style="width: auto !important; flex: none !important;"
                  >
                    <v-icon color="white" small>mdi-plus</v-icon>
                    <span class="font-bold text-sm">NOVO</span>
                  </button>
                  
                  <div class="relative w-80">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <v-icon color="white" small class="opacity-50">mdi-magnify</v-icon>
                    </div>
                    <input 
                      v-model="search"
                      placeholder="PESQUISAR PREFEITURA..."
                      class="bg-white/10 border border-white/20 rounded-lg py-2 pl-10 pr-4 text-[11px] text-white placeholder:text-white/40 focus:outline-none focus:bg-white/20 transition-all w-full uppercase tracking-widest"
                    >
                  </div>
                </div>
            </div>

            <div class="p-0">
                <v-data-table
                  :headers="headers"
                  :items="data"
                  :search="search"
                  :page.sync="page"
                  :items-per-page="itemsPerPage"
                  @page-count="pageCount = $event"
                  :loading="loader"
                  loading-text="CARREGANDO..."
                  class="custom-table"
                  hide-default-footer
                >
                  <template v-slot:item.prefecture_social_name="{ item }">
                    <div class="flex items-center gap-3 py-3 pl-2">
                      <div class="flex flex-col">
                        <span class="text-sm font-bold text-slate-700 uppercase tracking-wide leading-tight">{{ item.prefecture_social_name }}</span>
                        <span class="text-[10px] text-slate-400 font-medium tracking-wider">{{ item.prefecture_fantasy_name || 'NOME FANTASIA NÃO INFORMADO' }}</span>
                      </div>
                    </div>
                  </template>

                  <template v-slot:item.prefecture_cnpj="{ item }">
                    <span class="text-[11px] font-bold text-slate-600 tracking-wider">{{ item.prefecture_cnpj }}</span>
                  </template>

                  <template v-slot:item.prefecture_cel="{ item }">
                    <div class="flex flex-col">
                        <span class="text-[11px] font-bold text-slate-600 tracking-wider">{{ item.prefecture_cel || '-' }}</span>
                        <span class="text-[9px] text-slate-400 uppercase tracking-widest">{{ item.prefecture_mail || '' }}</span>
                    </div>
                  </template>

                  <template v-slot:item.action="{ item }">
                    <div class="flex justify-end gap-2">
                      <button 
                        @click="$router.push(`/prefectures-manager/secretariats/${item.prefecture_id}`)"
                        class="p-2 hover:bg-blue-50 text-blue-600 rounded-lg transition-colors"
                        title="Secretarias"
                      >
                        <v-icon small color="blue darken-1">mdi-playlist-check</v-icon>
                      </button>
                      <button 
                        @click="$router.push(`/prefectures-manager/edit/${item.prefecture_id}`)"
                        class="p-2 hover:bg-orange-50 text-orange-600 rounded-lg transition-colors"
                        title="Editar"
                      >
                        <v-icon small color="orange">mdi-pencil</v-icon>
                      </button>
                      <button 
                        @click="deleteItem(item.prefecture_id)"
                        class="p-2 hover:bg-red-50 text-red-600 rounded-lg transition-colors"
                        title="Excluir"
                      >
                        <v-icon small color="red">mdi-delete</v-icon>
                      </button>
                    </div>
                  </template>

                  <template v-slot:no-data>
                    <div class="py-12 flex flex-col items-center justify-center text-slate-400">
                      <v-icon size="48" color="slate-200">mdi-database-off</v-icon>
                      <p class="mt-2 text-sm">Nenhuma prefeitura encontrada</p>
                    </div>
                  </template>
                </v-data-table>

                <!-- Custom Pagination Footer -->
                <div class="bg-slate-50 px-6 py-8 border-t border-slate-200 flex flex-col items-center justify-center gap-6 text-xs text-slate-500 font-medium w-full">
                    <span>Exibindo {{ (page - 1) * itemsPerPage + 1 }} de {{ data.length }} registros</span>
                    
                    <div class="flex gap-1" v-if="pageCount > 1">
                        <button 
                          @click="page > 1 ? page-- : null" 
                          :disabled="page === 1"
                          class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold uppercase tracking-widest"
                        >
                          Anterior
                        </button>
                        
                        <button 
                          v-for="p in visiblePages" 
                          :key="p"
                          @click="page = p"
                          :class="p === page ? 'bg-slate-700 text-white border-slate-700 shadow-sm' : 'bg-white border-slate-200 hover:bg-slate-100'"
                          class="px-3 py-1.5 border rounded-md transition-colors font-bold"
                        >
                          {{ p }}
                        </button>

                        <button 
                          @click="page < pageCount ? page++ : null" 
                          :disabled="page === pageCount"
                          class="px-3 py-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-bold uppercase tracking-widest"
                        >
                          Próximo
                        </button>
                    </div>
                </div>
            </div>
        </section>

        <!-- NOTIFICAÇÕES -->
        <v-snackbar
          v-model="snack.show"
          :color="snack.color"
          timeout="3000"
          rounded="lg"
          elevation="24"
        >
          <div class="flex items-center gap-3">
            <v-icon color="white" small>
              {{ snack.color === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
            </v-icon>
            <span class="text-xs font-bold uppercase tracking-wider">{{ snack.message }}</span>
          </div>
        </v-snackbar>
      </div>
    </div>
  </v-container>
</template>

<script>
export default {
  data() {
    return {
      search: "",
      loader: true,
      data: [],
      snack: {
        show: false,
        message: "",
        color: ""
      },
      headers: [
        { text: "Razão Social / Nome Fantasia", value: "prefecture_social_name" },
        { text: "CNPJ", value: "prefecture_cnpj" },
        { text: "Contato", value: "prefecture_cel" },
        { text: "Ações", align: "right", value: "action", sortable: false }
      ],
      page: 1,
      pageCount: 0,
      itemsPerPage: 10,
    };
  },
  computed: {
    visiblePages() {
      const pages = [];
      const totalVisible = 5;
      let start = Math.max(1, this.page - Math.floor(totalVisible / 2));
      let end = Math.min(this.pageCount, start + totalVisible - 1);
      if (end - start + 1 < totalVisible) start = Math.max(1, end - totalVisible + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loader = true;
      this.$store.dispatch("setLoader", this.loader);
      this.$http
        .get("/prefectures")
        .then(r => {
          this.data = r.data;
          this.loader = false;
          this.$store.dispatch("setLoader", this.loader);
        })
        .catch(e => {
          this.loader = false;
          this.snack.show = true;
          this.snack.message = e;
          this.snack.color = "error";
          this.$store.dispatch("setLoader", this.loader);
        });
    },
    deleteItem(id) {
      let c = confirm("Deseja excluir o registro?");
      if (c) {
        this.$store.dispatch("setLoader", true);
        this.$http
          .delete(`/prefectures/${id}`)
          .then(r => {
            this.snack.show = true;
            this.snack.message = r.data.result;
            this.snack.color = "success";
            this.initialize();
          })
          .catch(e => {
            this.snack.show = true;
            this.snack.message = e.message;
            this.snack.color = "error";
            this.$store.dispatch("setLoader", false);
          });
      }
    }
  }
};
</script>

<style scoped>
.min-h-screen { min-height: 100vh; }
.bg-slate-50 { background-color: #f8fafc; }
.border-slate-200 { border-color: #e2e8f0; }
.text-slate-500 { color: #64748b; }
.text-slate-700 { color: #334155; }

.custom-table ::v-deep table { background-color: transparent !important; border-collapse: separate; border-spacing: 0; }
.custom-table ::v-deep th { 
  color: #64748b !important; 
  font-weight: 700 !important; 
  text-transform: uppercase !important; 
  font-size: 0.75rem !important; 
  background-color: #f8fafc !important;
  border-bottom: 1px solid #e2e8f0 !important;
  height: 48px !important;
  padding: 0 24px !important;
}
.custom-table ::v-deep td { 
  border-bottom: 1px solid #f1f5f9 !important;
  color: #334155 !important;
  font-size: 0.875rem !important;
  padding: 0 24px !important;
}
.custom-table ::v-deep .v-data-table__wrapper { border-radius: 0 !important; }

/* Custom Checkbox styles for Vuetify if needed */
</style>